/**
 * OR-6126: read-only review surface for a *proposed* concept group, rendered in
 * the project page's right pane (the same outlet that shows cohort / AV /
 * created-concept-group detail pages under ``research_assistant_gen_3``).
 *
 * The agent proposes a group → a ``concept_group`` chat chip routes here with the
 * ``previewId``. The epi reviews the per-seed Definition + the flat Concepts
 * list, optionally re-uploads a source file, then **Saves** the group into the
 * org library via the header's primary. Once created the model redirects to the
 * real ``/concept-group/:id`` route and the chat resumes server-side.
 *
 * This container is a thin orchestrator: data + behaviour live in the
 * ``concept-group/hooks`` (preview model, file-replace, ownership); the header,
 * source-file bar, and the three tabs are their own components / containers.
 * One implementation, two mounts (OR-6288 single-view-while-Draft):
 *   - the legacy ``/concept-group-preview/:previewId`` route, and
 *   - the canonical ``/concept-group/:id`` page, which renders this while Draft.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { Alert, Box, CircularProgress, Skeleton, Snackbar, Stack, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ConceptGroupSourceFileBar } from '@verantos-dev/feature-concept-group-detail';
import { downloadPreviewCsv } from '@verantos-dev/feature-epi-chat';

import { DraftConceptsTab } from './DraftConceptsTab';
import { DraftMappingTab } from './DraftMappingTab';
import { DraftRulesTab } from './DraftRulesTab';
import { ConceptGroupDraftHeader } from '../components/ConceptGroupDraftHeader';
import { useConceptGroupPreviewPage } from '../hooks/useConceptGroupPreviewPage';
import { useDraftConceptGroupOwnership } from '../hooks/useDraftConceptGroupOwnership';
import { useDraftFileReplace } from '../hooks/useDraftFileReplace';

type PreviewTab = 'rules' | 'concepts' | 'mapping';

export const ConceptGroupDraftReview = ({
  previewId,
  projectId,
}: {
  previewId: string;
  projectId: string;
}) => {
  const { formatMessage } = useIntl();
  const model = useConceptGroupPreviewPage({ previewId, projectId });
  const { fileInputRef, onFilePicked, openPicker, isReplacing, fileError } = useDraftFileReplace({
    projectId,
    previewId,
    currentName: model.name,
  });
  // Only the draft's owner may Save or Change file; non-owners get a read-only
  // review (OR-6288 permissions).
  const { isOwner: isDraftOwner } = useDraftConceptGroupOwnership(projectId, {
    createdGroupId: model.createdGroupId,
    previewId: model.detail?.previewId,
    conceptGroupName: model.detail?.conceptGroupName,
  });
  const [tab, setTab] = useState<PreviewTab>('rules');

  // First load of the preview detail (opening a Draft from the artifact list):
  // until it resolves, membershipMode/sourceFilename/owner are all unknown, so
  // the surface would otherwise render misleading defaults — the fallback
  // filename, the Definition tab (isUpload defaults false), a current-user owner
  // — which read as a *different* concept group flashing before the real one.
  // Skeleton the body instead so the loading state reads as "loading", not
  // "some other group". (When the destination was prefetched, detail is already
  // warm and this is false — no skeleton, no flash.)
  const isDetailLoading = model.isLoading && !model.detail;

  // OR-6125: an uploaded group is a ``literal`` exact list — it has no per-seed
  // hierarchy rules, so the Rules tab is suppressed and the view defaults to
  // Concepts. A map-first upload additionally has a source→mapped audit
  // (``hasMappingDetail``) shown in a "Mapping results" tab. ``effectiveTab``
  // derives the shown tab (rather than syncing state on load) so there's no flash
  // when the detail resolves, and falls back to Concepts if the selected tab is
  // hidden for this preview.
  // OR-6288 finding #2: the review surface must show the name the user will
  // PUBLISH, not the draft's stored server name. ``model.name`` is seeded from
  // the detail on load and holds any Step-1 rename (which publish submits), so
  // prefer it; fall back to the stored name only during the pre-seed window.
  const displayName = model.name || model.detail?.conceptGroupName || '';
  const isUpload = model.detail?.membershipMode === 'literal';
  const hasMapping = Boolean(model.detail?.hasMappingDetail);
  const showRules = !isUpload;
  // OR-6288 design: a mapped upload REVIEWS the mapping first (Mapping results
  // is the leading tab and the landing tab); an all-standard upload has no
  // mapping — just the flat Concepts list; an agent draft leads with Definition.
  const uploadLandingTab: PreviewTab = hasMapping ? 'mapping' : 'concepts';
  const effectiveTab: PreviewTab =
    tab === 'rules' && !showRules
      ? uploadLandingTab
      : tab === 'mapping' && !hasMapping
      ? 'concepts'
      : tab;

  // Once the proposal is CONFIRMED (or this is a deep-link to an already-confirmed
  // preview) ``useConceptGroupPreviewPage`` redirects to the real concept-group
  // route. Show a brief spinner while that navigation lands. Gate on
  // ``isConfirmed`` (the redirect's own condition), NOT on ``createdGroupId``
  // alone: since OR-6289 the id is minted at save, so a pending Draft carries a
  // ``createdGroupId`` while never redirecting.
  const resolved =
    model.isConfirmed && model.createdGroupId ? (
      <Box
        data-testid="project-concept-group-preview-redirecting"
        sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    ) : (
      <Box
        data-testid="project-concept-group-preview-page"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <ConceptGroupDraftHeader
          model={model}
          displayName={displayName}
          projectId={projectId}
          // Only the owner sees Save.
          canEdit={isDraftOwner}
          // A replacement upload is in flight — saving now would race it.
          saveDisabled={isReplacing}
        />

        <Box sx={{ display: 'contents' }}>
          <ConceptGroupSourceFileBar
            sourceFilename={
              model.detail?.sourceFilename ?? `${displayName || 'concept-group-draft'}.xlsx`
            }
            // Skeleton the filename until the detail lands (no fallback flash).
            isLoading={isDetailLoading}
            // Align with the header + tabs (24px gutter), matching the Ready view.
            gutter={6}
            canEdit={isDraftOwner}
            isReplacing={isReplacing}
            fileError={fileError}
            fileInputRef={fileInputRef}
            onFilePicked={onFilePicked}
            onOpenPicker={openPicker}
            inputKey={previewId}
            // Clicking the filename runs the same download as the Concepts
            // grid's download icon (preview CSV export).
            onDownload={
              previewId
                ? () => void downloadPreviewCsv(previewId, displayName || 'concepts')
                : undefined
            }
          />
          {/* Skeleton the body while (a) a replacement uploads in the background
              — the content is stale for the superseded draft until the new
              draft's route lands — or (b) the preview detail is still loading on
              first open, so the tabs/content don't flash misleading defaults. */}
          {isReplacing || isDetailLoading ? (
            <Stack
              sx={{ flex: 1, minHeight: 0, paddingX: 6, gap: 2 }}
              data-testid="cg-preview-replacing-skeleton"
            >
              <Skeleton variant="rectangular" height={42} />
              <Skeleton variant="rectangular" sx={{ flex: 1 }} />
            </Stack>
          ) : (
            <>
              <Tabs
                value={effectiveTab}
                onChange={(_, value) => setTab(value as PreviewTab)}
                sx={{ paddingX: 6, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                data-testid="cg-preview-tabs"
              >
                {!isUpload && (
                  <Tab
                    value="rules"
                    label={formatMessage({ id: 'epiChat.conceptGroupPreview.tab.rules' })}
                    sx={{
                      textTransform: 'uppercase',
                      typography: 'body2',
                      fontWeight: 500,
                      letterSpacing: '0.4px',
                    }}
                    data-testid="cg-preview-tab-rules"
                  />
                )}
                {/* OR-6288 design: Mapping results LEADS for a mapped upload (review the
                    source→standard audit first); Concepts follows. */}
                {hasMapping && (
                  <Tab
                    value="mapping"
                    label={formatMessage({ id: 'epiChat.conceptGroupPreview.tab.mapping' })}
                    sx={{
                      textTransform: 'uppercase',
                      typography: 'body2',
                      fontWeight: 500,
                      letterSpacing: '0.4px',
                    }}
                    data-testid="cg-preview-tab-mapping"
                  />
                )}
                <Tab
                  value="concepts"
                  label={formatMessage({ id: 'epiChat.conceptGroupPreview.tab.concepts' })}
                  sx={{
                    textTransform: 'uppercase',
                    typography: 'body2',
                    fontWeight: 500,
                    letterSpacing: '0.4px',
                  }}
                  data-testid="cg-preview-tab-concepts"
                />
              </Tabs>

              {/* No bottom padding here: every tab's content ends at this box's edge
                  and the footer's own padding provides the (identical) gap. */}
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', paddingX: 6, paddingTop: 2 }}>
                {effectiveTab === 'rules' && (
                  <DraftRulesTab
                    loading={model.isLoading}
                    error={model.isError}
                    ruleItems={model.detail?.ruleItems ?? []}
                    previewId={previewId || null}
                    conceptGroupName={displayName}
                  />
                )}
                {effectiveTab === 'concepts' && (
                  <DraftConceptsTab
                    previewId={previewId || null}
                    groupName={displayName || 'concepts'}
                    invalidValues={model.detail?.invalidValues ?? []}
                    conceptCount={model.detail?.totalCount ?? null}
                  />
                )}
                {effectiveTab === 'mapping' && (
                  <DraftMappingTab
                    previewId={previewId || null}
                    enabled={hasMapping}
                    conceptGroupName={displayName}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    );

  return (
    <>
      {resolved}

      <Snackbar
        open={Boolean(model.createdName)}
        autoHideDuration={6000}
        onClose={model.dismissCreatedToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={model.dismissCreatedToast}
          data-testid="cg-preview-created-toast"
        >
          {formatMessage(
            { id: 'epiChat.conceptGroupPreview.createdToast' },
            { name: model.createdName ?? '' }
          )}
        </Alert>
      </Snackbar>
    </>
  );
};
